/* ============================================================
   MediaGallery — ME-3 marketplace-style product gallery:
   photo/video stage + thumbnail strip (DESIGN-SYSTEM light
   theme, card/btn primitives, no new deps).
   ============================================================ */

import { useState, type CSSProperties } from 'react';
import { resolveMediaUrl } from '../api/client';
import type { ProductDetail } from '../types/product';
import { mediaGalleryView } from './mediaGalleryView';
import { Model3DPreview } from './Model3DPreview';
import { modelFormatFromUrl } from './shop3dView';

interface Props {
  product: Pick<ProductDetail, 'media' | 'imageUrl' | 'title'>;
  /** cca635b3: color name tinting the 3D stage (buyer's chosen color option
   *  value on the shop page, or the product's base color). Omitted = neutral. */
  model3dTint?: string | null;
  /** cca635b3: photo shown instead of the mesh on no-WebGL devices/failures. */
  model3dFallbackUrl?: string | null;
}

const STAGE_HEIGHT = 320;

export function MediaGallery({ product, model3dTint, model3dFallbackUrl }: Props) {
  const gallery = mediaGalleryView(product);
  const [selected, setSelected] = useState<number | null>(null);

  if (gallery.items.length === 0) {
    return (
      <div
        aria-hidden
        style={{
          height: STAGE_HEIGHT,
          borderRadius: 'var(--radius-md)',
          background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: 72,
          fontWeight: 'var(--fw-bold)',
          marginBottom: 'var(--space-lg)',
        }}
      >
        {product.title.slice(0, 1).toUpperCase()}
      </div>
    );
  }

  const active = selected === null ? gallery.initialIndex : selected;
  const activeItem = gallery.items[active];

  // cca635b3: viewable-model rows mount the interactive 3D stage.
  const tileChipStyle: CSSProperties = {
    position: 'absolute',
    right: 4,
    bottom: 4,
    fontSize: 'var(--fs-caption)',
    background: 'rgba(0,0,0,0.55)',
    color: '#fff',
    borderRadius: 'var(--radius-sm)',
    padding: '1px 5px',
  };

  return (
    <div style={{ marginBottom: 'var(--space-lg)' }}>
      {/* Stage */}
      {activeItem.kind === 'model' ? (
        <Model3DPreview
          modelUrl={activeItem.url}
          format={modelFormatFromUrl(activeItem.url)}
          colorName={model3dTint ?? null}
          alt={product.title}
          height={STAGE_HEIGHT}
          previewUrl={model3dFallbackUrl ?? null}
        />
      ) : activeItem.kind === 'video' ? (
        <video
          key={gallery.items[active].url}
          src={resolveMediaUrl(gallery.items[active].url)}
          controls
          playsInline
          preload="metadata"
          style={{
            width: '100%',
            height: STAGE_HEIGHT,
            objectFit: 'contain',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-separator)',
          }}
        />
      ) : (
        <img
          key={gallery.items[active].url}
          src={resolveMediaUrl(gallery.items[active].url)}
          alt={product.title}
          style={{
            width: '100%',
            height: STAGE_HEIGHT,
            objectFit: 'cover',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-separator)',
          }}
        />
      )}

      {/* Thumbnail strip */}
      {gallery.items.length > 1 && (
        <div style={{ display: 'flex', gap: 'var(--space-xs)', marginTop: 'var(--space-sm)', overflowX: 'auto', paddingBottom: 2 }}>
          {gallery.items.map((item, i) => (
            <button
              key={`${item.url}-${i}`}
              onClick={() => setSelected(i)}
              aria-label={item.kind === 'video' ? 'Видео' : item.kind === 'model' ? '3D-модель' : 'Фото'}
              className="card"
              style={{
                flexShrink: 0,
                width: 64,
                height: 64,
                padding: 0,
                overflow: 'hidden',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                border: i === active
                  ? '2px solid var(--color-primary)'
                  : '1px solid var(--color-separator)',
                position: 'relative',
              }}
            >
              {item.kind === 'model' ? (
                // cca635b3: model rows have no raster thumb — a 3D tile instead.
                <span
                  aria-hidden
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                    background: '#F2F2F7',
                    color: 'var(--color-primary)',
                    fontSize: 'var(--fs-caption)',
                    fontWeight: 'var(--fw-semibold)',
                  }}
                >
                  3D
                </span>
              ) : (
                <img
                  src={resolveMediaUrl(item.thumbUrl)}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              )}
              {item.kind === 'video' && <span style={tileChipStyle}>▶</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
